import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { fetchPortalUsers, fetchTenants, createPortalUser, updatePortalUser, resetUserPassword } from '../../services/pocketbase';
import { USER_ROLES, USER_ROLE_FILTER_OPTIONS, CLIENT_LOGIN_URL, getUserRoleLabel, isPlatformAdminRole, isSuperAdminRole } from '../../config/constants';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiUsers, FiEdit3, FiX, FiSave, FiSearch, FiFilter, FiKey, FiCopy, FiMail, FiCheckCircle } = FiIcons;

const AdminUsers = () => {
  const { currentUserRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [filters, setFilters] = useState({ tenant: '', role: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Email Helper State
  const [emailModalData, setEmailModalData] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  const assignableUserRoles = isSuperAdminRole(currentUserRole)
    ? USER_ROLES
    : USER_ROLES.filter(role => role.value !== 'bpas_admin');
  const [formData, setFormData] = useState({
    email: '',
    display_name: '',
    role: 'staff',
    tenant: '',
    password: ''
  });
  const platformRoleSelected = isPlatformAdminRole(formData.role);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, filters, searchTerm]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [usersData, tenantsData] = await Promise.all([
        fetchPortalUsers(),
        fetchTenants()
      ]);
      setUsers(usersData);
      setTenants(tenantsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];
    
    if (filters.tenant) {
      filtered = filtered.filter(user => user.tenant === filters.tenant);
    }
    
    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.expand?.tenant?.business_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredUsers(filtered);
  };

  const generatePassword = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const bytes = new Uint32Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, value => alphabet[value % alphabet.length]).join('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (isSuperAdminRole(formData.role)) {
        throw new Error('Super Admin is a reserved system role and cannot be assigned from User Management.');
      }
      if (formData.role === 'bpas_admin' && !isSuperAdminRole(currentUserRole)) {
        throw new Error('Only a Super Admin can assign the BPAS Admin role.');
      }
      if (!isPlatformAdminRole(formData.role) && !formData.tenant) {
        throw new Error('A tenant is required for Owner and Staff users.');
      }

      if (editingUser) {
        if (isPlatformAdminRole(editingUser.role) && !isSuperAdminRole(currentUserRole)) {
          throw new Error('Only a Super Admin can manage platform-admin accounts.');
        }
        const userData = {
          email: formData.email,
          display_name: formData.display_name,
          role: formData.role,
          tenant: isPlatformAdminRole(formData.role) ? '' : formData.tenant
        };

        if (formData.password) {
          userData.password = formData.password;
          userData.passwordConfirm = formData.password;
        }

        const updatedUser = await updatePortalUser(editingUser.id, userData);
        setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));
        setSuccess('User updated successfully');
      } else {
        const password = formData.password || generatePassword();
        const userData = {
          email: formData.email,
          display_name: formData.display_name,
          role: formData.role,
          tenant: isPlatformAdminRole(formData.role) ? '' : formData.tenant,
          password,
          passwordConfirm: password
        };

        const newUser = await createPortalUser(userData);
        setUsers([newUser, ...users]);
        setSuccess('User created successfully.');
        
        // Open Email Helper Modal
        const tenantName = tenants.find(t => t.id === userData.tenant)?.business_name || 'your portal';
        setEmailModalData({
          email: userData.email,
          displayName: formData.display_name || '',
          tenantName,
          tempPassword: password,
          loginUrl: CLIENT_LOGIN_URL,
          context: 'created'
        });
        setIsEmailModalOpen(true);
      }
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (userId, email) => {
    const targetUser = users.find(user => user.id === userId);
    if (isSuperAdminRole(targetUser?.role)) {
      setError('Super Admin credentials cannot be reset from User Management.');
      return;
    }
    if (isPlatformAdminRole(targetUser?.role) && !isSuperAdminRole(currentUserRole)) {
      setError('Only a Super Admin can reset a platform-admin password.');
      return;
    }
    if (!window.confirm(`Are you sure you want to reset the password for ${email}? A new password will be generated and shown once.`)) {
      return;
    }

    setResettingPassword(userId);
    setError('');
    setSuccess('');

    try {
      const newPassword = generatePassword();
      await resetUserPassword(userId, newPassword);
      setSuccess(`Password reset successfully for ${email}.`);
      
      // Find tenant name for this user
      const user = users.find(u => u.id === userId);
      const tenantName = user?.expand?.tenant?.business_name || 'your portal';
      
      // Open Email Helper Modal
      setEmailModalData({
        email,
        displayName: user?.display_name || '',
        tenantName,
        tempPassword: newPassword,
        loginUrl: CLIENT_LOGIN_URL,
        context: 'reset'
      });
      setIsEmailModalOpen(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setResettingPassword(null);
    }
  };

  const handleEdit = (user) => {
    if (isSuperAdminRole(user.role)) {
      setError('Super Admin is a reserved system role and cannot be edited from User Management.');
      return;
    }
    if (isPlatformAdminRole(user.role) && !isSuperAdminRole(currentUserRole)) {
      setError('Only a Super Admin can manage platform-admin accounts.');
      return;
    }
    setEditingUser(user);
    setFormData({
      email: user.email,
      display_name: user.display_name || '',
      role: user.role,
      tenant: user.tenant,
      password: ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      display_name: '',
      role: 'staff',
      tenant: '',
      password: ''
    });
    setEditingUser(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'role' && isPlatformAdminRole(value)) {
        return { ...prev, role: value, tenant: '' };
      }
      return { ...prev, [name]: value };
    });
    if (error) setError('');
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'super_admin': 'bg-indigo-100 text-indigo-800',
      'owner': 'bg-purple-100 text-purple-800',
      'staff': 'bg-blue-100 text-blue-800',
      'bpas_admin': 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  // Email helper handlers
  const handleCopyLoginDetails = async () => {
    if (!emailModalData) return;
    const text = `Tenant: ${emailModalData.tenantName}\nLogin URL: ${emailModalData.loginUrl}\nEmail: ${emailModalData.email}\nTemporary password: ${emailModalData.tempPassword}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback('Login details copied to clipboard.');
      setTimeout(() => setCopyFeedback(''), 3000);
    } catch (err) {
      setCopyFeedback('Could not copy. Please copy manually.');
    }
  };

  const handleCopyEmail = async () => {
    if (!emailModalData) return;
    
    const greeting = emailModalData.displayName
      ? `Hi ${emailModalData.displayName},`
      : 'Hi there,';

    const actionSentence = emailModalData.context === 'reset'
      ? 'Your password has been reset for BPAS Client Hub.'
      : 'Your BPAS Client Hub account has been created.';

    const body = `${greeting}

${actionSentence}

You can log in here:
${emailModalData.loginUrl}

Email: ${emailModalData.email}
Temporary password: ${emailModalData.tempPassword}

Please log in and change your password the first time you use the portal.

If you have any questions or need support, just reply to this email.

Regards
BPA Solutions`;

    try {
      await navigator.clipboard.writeText(body);
      setCopyFeedback('Email copied to clipboard.');
      setTimeout(() => setCopyFeedback(''), 3000);
    } catch (err) {
      setCopyFeedback('Could not copy. Please copy manually.');
    }
  };

  const handleOpenEmailDraft = () => {
    if (!emailModalData || !emailModalData.email) return;
    
    const greeting = emailModalData.displayName
      ? `Hi ${emailModalData.displayName},`
      : 'Hi there,';

    const actionSentence = emailModalData.context === 'reset'
      ? 'Your password has been reset for BPAS Client Hub.'
      : 'Your BPAS Client Hub account has been created.';

    const body = `${greeting}

${actionSentence}

You can log in here:
${emailModalData.loginUrl}

Email: ${emailModalData.email}
Temporary password: ${emailModalData.tempPassword}

Please log in and change your password the first time you use the portal.

If you have any questions or need support, just reply to this email.

Regards
BPA Solutions`;

    const subject = emailModalData.context === 'reset'
      ? 'Your BPAS Client Hub password reset'
      : 'Your BPAS Client Hub login details';

    const mailto = `mailto:${emailModalData.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setEmailModalData(null);
    setCopyFeedback('');
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-6 w-48"></div>
          <div className="bg-gray-200 h-64 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create User</span>
          </motion.button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-700 whitespace-pre-line">{error}</p>
          </div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-md p-4 mb-6"
          >
            <p className="text-green-700 whitespace-pre-line">{success}</p>
          </motion.div>
        )}

        {/* Search */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <select
                value={filters.tenant}
                onChange={(e) => setFilters({ ...filters, tenant: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Tenants</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.business_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Roles</option>
                {USER_ROLE_FILTER_OPTIONS.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* User Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {editingUser ? 'Edit User' : 'Create New User'}
                    </h2>
                    <button
                      onClick={resetForm}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="user@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Display Name
                      </label>
                      <input
                        type="text"
                        name="display_name"
                        value={formData.display_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Full Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tenant {platformRoleSelected ? '(not required for platform admins)' : '*'}
                      </label>
                      <select
                        name="tenant"
                        value={platformRoleSelected ? '' : formData.tenant}
                        onChange={handleChange}
                        required={!platformRoleSelected}
                        disabled={platformRoleSelected}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                      >
                        <option value="">{platformRoleSelected ? 'No tenant required' : 'Select Tenant'}</option>
                        {tenants.map(tenant => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.business_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role *
                      </label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {assignableUserRoles.map(role => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password {editingUser && '(leave blank to keep current)'}
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={editingUser ? "Leave blank to keep current" : "Leave blank to auto-generate"}
                      />
                      {!editingUser && (
                        <p className="text-xs text-gray-500 mt-1">
                          If left blank, a secure password will be generated automatically
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={isSubmitting}
                        className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiSave} className="w-4 h-4" />
                        <span>{isSubmitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}</span>
                      </motion.button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Email Helper Modal */}
        <AnimatePresence>
          {isEmailModalOpen && emailModalData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={handleCloseEmailModal}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                      <SafeIcon icon={FiMail} className="w-5 h-5 mr-2" />
                      {emailModalData.context === 'reset' ? 'User password reset' : 'User login details created'}
                    </h2>
                    <button
                      onClick={handleCloseEmailModal}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                    <div className="space-y-2 text-sm text-gray-900">
                      <p><span className="font-semibold">Tenant:</span> {emailModalData.tenantName}</p>
                      {emailModalData.displayName && (
                         <p><span className="font-semibold">User:</span> {emailModalData.displayName}</p>
                      )}
                      <p><span className="font-semibold">Email:</span> {emailModalData.email}</p>
                      <p><span className="font-semibold">Login URL:</span> {emailModalData.loginUrl}</p>
                      <p><span className="font-semibold">Temporary password:</span> <span className="font-mono bg-blue-100 px-1 rounded">{emailModalData.tempPassword}</span></p>
                    </div>
                    <p className="mt-3 text-sm text-blue-700 italic">
                      Share these credentials with the user and ask them to change the password after first login.
                    </p>
                  </div>

                  {copyFeedback && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-md flex items-center text-sm"
                    >
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-2" />
                      {copyFeedback}
                    </motion.div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleCopyLoginDetails}
                      className="flex-1 flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      <SafeIcon icon={FiCopy} className="w-4 h-4" />
                      <span>Copy login details</span>
                    </button>
                    
                    <button
                      onClick={handleCopyEmail}
                      className="flex-1 flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      <SafeIcon icon={FiMail} className="w-4 h-4" />
                      <span>Copy email</span>
                    </button>

                    <button
                      onClick={handleOpenEmailDraft}
                      disabled={!emailModalData.email}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                        emailModalData.email
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <SafeIcon icon={FiMail} className="w-4 h-4" />
                      <span>Open email draft</span>
                    </button>
                  </div>

                  <div className="mt-4 text-center">
                     <button
                      onClick={handleCloseEmailModal}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Users List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                {users.length === 0 ? 'No users found' : 'No users match your filters'}
              </p>
              {users.length === 0 && (
                <button
                  onClick={() => setShowForm(true)}
                  className="text-blue-600 hover:text-blue-500 font-medium"
                >
                  Create your first user
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tenant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.email}</p>
                          {user.display_name && (
                            <p className="text-xs text-gray-500">{user.display_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                          {getUserRoleLabel(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {user.expand?.tenant?.business_name || 'No tenant'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="p-2 text-gray-400 hover:text-blue-600"
                            title="Edit user"
                            disabled={isSuperAdminRole(user.role) || (isPlatformAdminRole(user.role) && !isSuperAdminRole(currentUserRole))}
                          >
                            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(user.id, user.email)}
                            className="p-2 text-gray-400 hover:text-orange-600"
                            title="Reset password"
                            disabled={resettingPassword === user.id || isSuperAdminRole(user.role) || (isPlatformAdminRole(user.role) && !isSuperAdminRole(currentUserRole))}
                          >
                            {resettingPassword === user.id ? (
                              <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <SafeIcon icon={FiKey} className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminUsers;